import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, NgZone, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IonIcon, IonSpinner } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  chevronBackOutline,
  createOutline,
  desktopOutline,
  eyeOutline,
  moonOutline,
  phonePortraitOutline,
  sunnyOutline,
  tabletPortraitOutline
} from 'ionicons/icons';
import { Observable, startWith } from 'rxjs';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { CanvasThemeMode } from '../../models/component-canvas-theme.models';
import {
  WebsiteBuilderNode,
  WebsiteBuilderDraftState,
  WebsitePageDocument,
  buildWebsiteBuilderNodeStyles,
  isWebsiteBuilderContainerNode,
  normalizeWebsitePageDocument
} from '../../models/website-builder.models';
import {
  PageRevisionSummary,
  WebsitePageRevisionDocumentState,
  WebsitePageSummary,
  WebsiteSummary
} from '../../models/website-management.models';
import { ComponentCanvasThemeService } from '../../services/component-canvas-theme.service';
import { WebsiteBuilderApiService } from '../../services/website-builder-api.service';
import { WebsiteManagementService } from '../../services/website-management.service';
import { mapCanvasThemeDefaults } from '../../utils/canvas-theme-style.utils';
import { isUuidV7 } from '../../utils/public-id.utils';
import { environment } from '../../../environments/environment';

type PreviewMode = 'desktop' | 'tablet' | 'mobile';
type PreviewPageDocumentState = WebsiteBuilderDraftState | WebsitePageRevisionDocumentState;

interface PreviewPreset {
  id: PreviewMode;
  label: string;
  icon: string;
  width: number;
  height: number;
}

@Component({
  selector: 'app-website-preview-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    IonIcon,
    IonSpinner,
    ApiLoadErrorComponent,
    InfoTooltipComponent
  ],
  templateUrl: './website-preview-page.component.html',
  styleUrls: ['./website-preview-page.component.scss']
})
export class WebsitePreviewPageComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly viewportStorageKey = 'bw.website-preview.viewport';
  private readonly minCanvasWidth = 320;
  private readonly maxCanvasWidth = 1600;
  private readonly minCanvasHeight = 560;
  private readonly maxCanvasHeight = 1800;

  readonly previewPresets: PreviewPreset[] = [
    { id: 'desktop', label: 'Desktop preview', icon: 'desktop-outline', width: 1360, height: 900 },
    { id: 'tablet', label: 'Tablet preview', icon: 'tablet-portrait-outline', width: 840, height: 1080 },
    { id: 'mobile', label: 'Mobile preview', icon: 'phone-portrait-outline', width: 390, height: 844 }
  ];

  isLoading = true;
  loadError: string | null = null;
  isPageLoading = false;
  pageError = '';
  website: WebsiteSummary | null = null;
  pages: WebsitePageSummary[] = [];
  currentPage: WebsitePageSummary | null = null;
  currentRevision: PageRevisionSummary | null = null;
  currentDocument: WebsitePageDocument | null = null;
  previewMode: PreviewMode = 'desktop';
  canvasWidth = 1360;
  canvasHeight = 900;

  private requestedWebsitePublicId: string | null = null;
  private requestedPagePublicId: string | null = null;
  private requestedRevisionPublicId: string | null = null;
  private loadCycle = 0;
  private readonly pageCache = new Map<string, { page: WebsitePageSummary; revision: PageRevisionSummary | null; document: WebsitePageDocument }>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly websiteManagementService: WebsiteManagementService,
    private readonly websiteBuilderApiService: WebsiteBuilderApiService,
    readonly canvasThemeService: ComponentCanvasThemeService
  ) {
    addIcons({
      chevronBackOutline,
      createOutline,
      desktopOutline,
      eyeOutline,
      moonOutline,
      phonePortraitOutline,
      sunnyOutline,
      tabletPortraitOutline
    });

    this.restoreStoredViewport();

    this.route.queryParamMap
      .pipe(startWith(this.route.snapshot.queryParamMap), takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.requestedWebsitePublicId = this.parsePublicId(params.get('website'));
        this.requestedPagePublicId = this.parsePublicId(params.get('page'));
        this.requestedRevisionPublicId = this.parsePublicId(params.get('revision'));
        this.loadPreview();
      });
  }

  ngOnInit(): void {
    this.canvasThemeService.loadPackages()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  get currentCanvasThemePackageId(): string {
    return this.canvasThemeService.selectedPackageId();
  }

  get currentCanvasThemeMode(): CanvasThemeMode {
    return this.canvasThemeService.previewMode();
  }

  get canvasShellStyle(): Record<string, string> {
    return this.canvasThemeService.buildCanvasVariableStyle();
  }

  get canvasViewportStyle(): Record<string, string> {
    return {
      'color-scheme': this.currentCanvasThemeMode,
      width: `${this.canvasWidth}px`,
      minHeight: `${this.canvasHeight}px`
    };
  }

  get currentPathLabel(): string {
    return this.currentPage?.path || '/';
  }

  get revisionPreviewLabel(): string | null {
    return this.currentRevision ? `Revision v${this.currentRevision.versionNumber}` : null;
  }

  retryLoad(): void {
    this.loadPreview(true);
  }

  openCurrentPageBuilder(): void {
    if (!this.website || !this.currentPage) {
      return;
    }

    this.router.navigate(['/website-builder'], {
      queryParams: {
        website: this.website.publicId,
        page: this.currentPage.publicId
      }
    });
  }

  setPreviewMode(mode: PreviewMode): void {
    this.previewMode = mode;
    const preset = this.previewPresets.find((entry) => entry.id === mode);
    if (!preset) {
      return;
    }

    this.canvasWidth = preset.width;
    this.canvasHeight = preset.height;
    this.persistViewport();
  }

  setCanvasThemeMode(mode: CanvasThemeMode): void {
    this.canvasThemeService.setPreviewMode(mode);
  }

  selectCanvasThemePackage(packageId: string): void {
    this.canvasThemeService.setSelectedPackage(packageId);
  }

  navigateToPage(pagePublicId: string, replaceUrl = false): void {
    if (!this.website || !pagePublicId || pagePublicId === this.currentPage?.publicId) {
      return;
    }

    this.router.navigate(['/website-preview'], {
      queryParams: {
        website: this.website.publicId,
        page: pagePublicId
      },
      replaceUrl
    });
  }

  handlePreviewButtonClick(node: WebsiteBuilderNode): void {
    this.activateNode(node);
  }

  handlePreviewNodeClick(node: WebsiteBuilderNode, event?: Event): void {
    if (!this.isInteractiveNode(node) || node.type === 'button') {
      return;
    }

    event?.stopPropagation();
    this.activateNode(node);
  }

  isInteractiveNode(node: WebsiteBuilderNode): boolean {
    return (node.content.linkKind || 'none') !== 'none';
  }

  previewNodeClasses(node: WebsiteBuilderNode): Record<string, boolean> {
    return {
      'is-interactive': this.isInteractiveNode(node),
      'has-hover-lift': (node.content.hoverEffect || 'none') === 'lift',
      'has-hover-grow': (node.content.hoverEffect || 'none') === 'grow',
      'has-hover-glow': (node.content.hoverEffect || 'none') === 'glow'
    };
  }

  nodeStyles(node: WebsiteBuilderNode): Record<string, string | number> {
    const styles = buildWebsiteBuilderNodeStyles(
      node,
      this.currentCanvasThemePackageId,
      this.currentCanvasThemeMode
    );
    mapCanvasThemeDefaults(styles);

    return styles;
  }

  resolveMediaUrl(value: string | undefined): string {
    if (!value || !value.startsWith('/')) {
      return value || '';
    }

    return new URL(value, environment.apiUrl).toString();
  }

  isContainer(node: WebsiteBuilderNode): boolean {
    return isWebsiteBuilderContainerNode(node);
  }

  resolveInternalTargetLabel(node: WebsiteBuilderNode): string {
    const targetPagePublicId = node.content.targetPagePublicId;
    if (!targetPagePublicId) {
      return 'No internal target';
    }

    const targetPage = this.pages.find((page) => page.publicId === targetPagePublicId);
    return targetPage ? `${targetPage.name} (${targetPage.path})` : 'Linked page';
  }

  trackPage(_index: number, page: WebsitePageSummary): string {
    return page.publicId;
  }

  trackNode(_index: number, node: WebsiteBuilderNode): string {
    return node.id;
  }

  private loadPreview(force = false): void {
    if (!this.requestedWebsitePublicId) {
      this.isLoading = false;
      this.loadError = 'Choose a website before opening the full preview.';
      return;
    }

    const sameWebsite = this.website?.publicId === this.requestedWebsitePublicId;
    if (sameWebsite && this.pages.length > 0 && !force) {
      const nextPagePublicId = this.resolveRequestedPagePublicId(this.pages, this.requestedPagePublicId);
      if (!nextPagePublicId) {
        this.loadError = 'This website has no pages to preview yet.';
        this.isLoading = false;
        return;
      }

      this.loadPageDocument(nextPagePublicId, false);
      return;
    }

    const cycle = ++this.loadCycle;
    this.isLoading = true;
    this.loadError = null;
    this.pageError = '';
    this.pageCache.clear();

    this.websiteManagementService.getPages(this.requestedWebsitePublicId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            if (cycle !== this.loadCycle) {
              return;
            }

            this.website = state.website;
            this.pages = state.pages;

            const nextPagePublicId = this.resolveRequestedPagePublicId(state.pages, this.requestedPagePublicId);
            if (!nextPagePublicId) {
              this.loadError = 'This website has no pages to preview yet.';
              this.isLoading = false;
              return;
            }

            this.loadPageDocument(nextPagePublicId, force, cycle);
          });
        },
        error: (error) => {
          this.runInAngular(() => {
            if (cycle !== this.loadCycle) {
              return;
            }

            this.loadError = error.message || 'The website could not be loaded for preview.';
            this.isLoading = false;
          });
        }
      });
  }

  private loadPageDocument(pagePublicId: string, force = false, cycleOverride?: number): void {
    if (!this.website) {
      return;
    }

    const revisionPublicId = this.requestedRevisionPublicId && this.requestedPagePublicId === pagePublicId
      ? this.requestedRevisionPublicId
      : null;
    const cacheKey = this.buildPageCacheKey(pagePublicId, revisionPublicId);
    const cached = this.pageCache.get(cacheKey);
    if (cached && !force) {
      this.currentPage = cached.page;
      this.currentRevision = cached.revision;
      this.currentDocument = structuredClone(cached.document);
      this.pageError = '';
      this.isLoading = false;
      this.isPageLoading = false;
      this.ensureCanonicalQueryParams(cached.page.publicId, cached.revision?.publicId ?? null);
      return;
    }

    const cycle = cycleOverride ?? ++this.loadCycle;
    this.isPageLoading = !this.isLoading;
    this.pageError = '';

    const documentRequest: Observable<PreviewPageDocumentState> = revisionPublicId
      ? this.websiteManagementService.getPageRevision(this.website.publicId, pagePublicId, revisionPublicId)
      : this.websiteBuilderApiService.getDraft(this.website.publicId, pagePublicId);

    documentRequest
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state: PreviewPageDocumentState) => {
          this.runInAngular(() => {
            if (cycle !== this.loadCycle) {
              return;
            }

            this.website = state.website;
            if ('pages' in state) {
              this.pages = state.pages;
            }
            this.currentPage = state.page;
            this.currentRevision = revisionPublicId && 'revision' in state ? state.revision : null;
            this.currentDocument = normalizeWebsitePageDocument(state.document as WebsitePageDocument);
            this.pageCache.set(cacheKey, {
              page: state.page,
              revision: this.currentRevision,
              document: structuredClone(this.currentDocument)
            });
            this.isLoading = false;
            this.isPageLoading = false;
            this.pageError = '';
            this.ensureCanonicalQueryParams(state.page.publicId, this.currentRevision?.publicId ?? null);
          });
        },
        error: (error: { message?: string }) => {
          this.runInAngular(() => {
            if (cycle !== this.loadCycle) {
              return;
            }

            this.isLoading = false;
            this.isPageLoading = false;
            this.pageError = error.message || 'The page draft could not be loaded for preview.';
            if (!this.currentDocument) {
              this.loadError = this.pageError;
            }
          });
        }
      });
  }

  private ensureCanonicalQueryParams(pagePublicId: string, revisionPublicId: string | null): void {
    if (!this.website) {
      return;
    }

    if (
      this.requestedPagePublicId === pagePublicId
      && this.requestedWebsitePublicId === this.website.publicId
      && this.requestedRevisionPublicId === revisionPublicId
    ) {
      return;
    }

    this.requestedWebsitePublicId = this.website.publicId;
    this.requestedPagePublicId = pagePublicId;
    this.requestedRevisionPublicId = revisionPublicId;

    this.router.navigate(['/website-preview'], {
      queryParams: {
        website: this.website.publicId,
        page: pagePublicId,
        revision: revisionPublicId || undefined
      },
      replaceUrl: true
    });
  }

  private buildPageCacheKey(pagePublicId: string, revisionPublicId: string | null): string {
    return revisionPublicId ? `${pagePublicId}:revision:${revisionPublicId}` : `${pagePublicId}:draft`;
  }

  private activateNode(node: WebsiteBuilderNode): void {
    const linkKind = node.content.linkKind || 'none';

    if (linkKind === 'internal-page' && node.content.targetPagePublicId) {
      this.navigateToPage(node.content.targetPagePublicId);
      return;
    }

    if (linkKind === 'external-url' && node.content.href && typeof window !== 'undefined') {
      const target = node.content.openInNewTab ? '_blank' : '_self';
      window.open(node.content.href, target, node.content.openInNewTab ? 'noopener,noreferrer' : undefined);
    }
  }

  private resolveRequestedPagePublicId(pages: WebsitePageSummary[], requestedPagePublicId: string | null): string | null {
    if (requestedPagePublicId && pages.some((page) => page.publicId === requestedPagePublicId)) {
      return requestedPagePublicId;
    }

    const homePagePublicId = this.website?.homePagePublicId ?? null;
    if (homePagePublicId && pages.some((page) => page.publicId === homePagePublicId)) {
      return homePagePublicId;
    }

    return pages[0]?.publicId ?? null;
  }

  private parsePublicId(value: string | null): string | null {
    if (!value || !isUuidV7(value)) {
      return null;
    }

    return value;
  }

  private restoreStoredViewport(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const storedWidth = Number(window.localStorage.getItem(`${this.viewportStorageKey}.width`));
    const storedHeight = Number(window.localStorage.getItem(`${this.viewportStorageKey}.height`));
    const storedMode = window.localStorage.getItem(`${this.viewportStorageKey}.mode`) as PreviewMode | null;

    if (!Number.isNaN(storedWidth) && storedWidth > 0) {
      this.canvasWidth = clamp(storedWidth, this.minCanvasWidth, this.maxCanvasWidth);
    }

    if (!Number.isNaN(storedHeight) && storedHeight > 0) {
      this.canvasHeight = clamp(storedHeight, this.minCanvasHeight, this.maxCanvasHeight);
    }

    if (storedMode && this.previewPresets.some((preset) => preset.id === storedMode)) {
      this.previewMode = storedMode;
    }
  }

  private persistViewport(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(`${this.viewportStorageKey}.width`, String(this.canvasWidth));
    window.localStorage.setItem(`${this.viewportStorageKey}.height`, String(this.canvasHeight));
    window.localStorage.setItem(`${this.viewportStorageKey}.mode`, this.previewMode);
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
